import { useEffect, useMemo, useState, type Key, type ReactElement, type ReactNode } from 'react';
import { Button, Card, Spinner } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useAppSelector } from '@hs/shared/stores/hooks';
import { trpc } from '@/lib/trpc';
import { PlanCheckoutModal, type PlanCheckoutProduct } from './PlanCheckoutModal';
import { PlanFeatureCompare, type FeatureConfigItem } from './PlanFeatureCompare';

type PlanSku = {
  id?: string | null;
  code?: string | null;
  name?: string | null;
  saleType?: string | null;
  billingPeriodUnit?: string | null;
  tier?: number | null;
  metadata?: unknown;
  skuPriceRule?: Array<{ unitPrice?: number | string | null }> | null;
};

type PlanCard = {
  key: string;
  members: PlanSku[];
  displaySku: PlanSku;
};

/** HeroUI Pro Segment.Item 当前类型未正确暴露 children，做运行时兼容包装 */
const BillingSegmentItem = Segment.Item as unknown as (props: {
  id: string;
  children?: ReactNode;
}) => ReactElement;

const PERIOD_LABEL: Record<string, string> = {
  MONTH: '月付',
  YEAR: '年付',
};

const PERIOD_PRICE_LABEL: Record<string, string> = {
  MONTH: '月',
  YEAR: '年',
  NONE: '次',
};

function toBillingPeriodUnit(value: string | null | undefined): 'MONTH' | 'YEAR' | 'NONE' {
  if (value === 'MONTH' || value === 'NONE') return value;
  return 'YEAR';
}

function formatYuan(unitPrice: number | string | null | undefined): number {
  if (unitPrice === undefined || unitPrice === null) return 0;
  const fen = typeof unitPrice === 'string' ? Number(unitPrice) : unitPrice;
  if (!Number.isFinite(fen)) return 0;
  return Math.round(fen) / 100;
}

function skuPriceRule(sku: PlanSku) {
  return sku.skuPriceRule?.[0] ?? null;
}

function isFreeSku(sku: PlanSku): boolean {
  return Number(skuPriceRule(sku)?.unitPrice ?? 0) <= 0 || Boolean(sku.code?.includes('FREE'));
}

function readFeatureConfig(value: unknown): FeatureConfigItem[] | null {
  if (!value || typeof value !== 'object') return null;
  const config = (value as { featureConfig?: unknown }).featureConfig;
  return Array.isArray(config) ? (config as FeatureConfigItem[]) : null;
}

function skuGroupKey(sku: PlanSku): string {
  if (sku.tier != null) return `tier:${sku.tier}`;
  return sku.code ?? String(sku.id ?? '');
}

function pickDisplaySku(members: PlanSku[], billingPeriodUnit: string): PlanSku {
  return (
    members.find(
      (sku) => sku.saleType === 'SUBSCRIPTION' && sku.billingPeriodUnit === billingPeriodUnit
    ) ??
    members.find((sku) => sku.saleType === 'SUBSCRIPTION') ??
    members.find((sku) => sku.saleType === 'ONE_TIME') ??
    members[0]
  );
}

function buildPlanCards(skus: PlanSku[], billingPeriodUnit: string): PlanCard[] {
  const groups = new Map<string, PlanSku[]>();
  for (const sku of skus) {
    const key = skuGroupKey(sku);
    const list = groups.get(key) ?? [];
    list.push(sku);
    groups.set(key, list);
  }

  const cards: PlanCard[] = [];
  for (const [key, members] of groups) {
    const displaySku = pickDisplaySku(members, billingPeriodUnit);
    if (!displaySku) continue;
    cards.push({ key, members, displaySku });
  }

  return cards.sort(
    (a, b) =>
      Number(a.displaySku.tier ?? 99) - Number(b.displaySku.tier ?? 99) ||
      (a.displaySku.code ?? '').localeCompare(b.displaySku.code ?? '')
  );
}

export function SpacePlanPanel() {
  const user = useAppSelector((state) => state.auth.user);
  const currentSpace = user?.spaces?.[0] ?? null;
  const subscription = currentSpace?.subscription ?? null;
  const planCode = subscription?.planCode ?? undefined;
  const planName = subscription?.planName?.trim() || '未订阅';
  const productCode = currentSpace?.type ? `${currentSpace.type}_PLAN` : '';

  const [billingPeriodUnit, setBillingPeriodUnit] = useState<string>('YEAR');
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [checkoutProduct, setCheckoutProduct] = useState<PlanCheckoutProduct | null>(null);

  const { data: product, isLoading, isError, error } = trpc.bizProduct.getByCode.useQuery(
    { code: productCode },
    { enabled: Boolean(productCode) }
  );

  const skus = (product?.skus ?? []) as PlanSku[];

  const periodUnits = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    for (const sku of skus) {
      if (sku.saleType !== 'SUBSCRIPTION') continue;
      const unit = sku.billingPeriodUnit;
      if (unit !== 'MONTH' && unit !== 'YEAR') continue;
      if (seen.has(unit)) continue;
      seen.add(unit);
      list.push(unit);
    }
    return list;
  }, [skus]);

  useEffect(() => {
    if (periodUnits.length === 0) return;
    if (!periodUnits.includes(billingPeriodUnit)) {
      setBillingPeriodUnit(periodUnits.includes('YEAR') ? 'YEAR' : periodUnits[0]);
    }
  }, [periodUnits, billingPeriodUnit]);

  const planCards = useMemo(
    () => buildPlanCards(skus, billingPeriodUnit),
    [skus, billingPeriodUnit]
  );

  const currentSku = useMemo(
    () => skus.find((sku) => sku.code === planCode) ?? null,
    [skus, planCode]
  );

  const handleBillingChange = (key: Key) => {
    setBillingPeriodUnit(String(key));
  };

  const openCheckoutModal = (sku: PlanSku) => {
    const rule = skuPriceRule(sku);
    setCheckoutProduct({
      id: String(sku.id),
      name: sku.name,
      code: sku.code ?? undefined,
      type: product?.type,
      uom: '席位',
      billingPeriodUnit: sku.billingPeriodUnit ?? undefined,
      prices: [
        {
          id: String(sku.id),
          billingMode: sku.billingPeriodUnit ?? undefined,
          unitPrice: formatYuan(rule?.unitPrice),
        },
      ],
    });
    setCheckoutModalOpen(true);
  };

  const renderCardActions = (card: PlanCard) => {
    const sku = card.displaySku;
    const isCurrent = Boolean(planCode && card.members.some((member) => member.code === planCode));
    const skuFree = isFreeSku(sku);
    const currentFree = Boolean(currentSku && isFreeSku(currentSku));

    if (currentFree) {
      if (isCurrent) {
        return (
          <Button variant="secondary" className="w-full" size="lg" onPress={() => {}}>
            ✓ 使用中
          </Button>
        );
      }
      if (skuFree) return null;
      return (
        <Button
          variant="primary"
          className="w-full"
          size="lg"
          onPress={() => openCheckoutModal(sku)}
        >
          升级为本计划
        </Button>
      );
    }

    if (isCurrent) {
      return (
        <div className="flex w-full flex-col gap-2 sm:flex-row">
          <Button variant="secondary" className="w-full" size="lg" onPress={() => {}}>
            ✓ 使用中
          </Button>
          {sku.saleType === 'SUBSCRIPTION' ? (
            <Button
              variant="primary"
              className="w-full"
              size="lg"
              onPress={() => openCheckoutModal(sku)}
            >
              续订
            </Button>
          ) : null}
        </div>
      );
    }

    if (skuFree) return null;

    const currentTier = currentSku?.tier ?? -1;
    const skuTier = sku.tier ?? -1;
    const label = skuTier > currentTier ? '升级为本计划' : '调整为本计划';

    return (
      <Button
        variant="primary"
        className="w-full"
        size="lg"
        onPress={() => openCheckoutModal(sku)}
      >
        {label}
      </Button>
    );
  };

  const renderCards = () => {
    if (planCards.length === 0) {
      return (
        <div className="flex min-h-[12rem] items-center justify-center">
          <p className="text-sm text-default-500">暂无该类型套餐</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {planCards.map((card) => {
          const sku = card.displaySku;
          const unitPrice = formatYuan(skuPriceRule(sku)?.unitPrice);
          const actions = renderCardActions(card);
          const priceUnit =
            sku.saleType === 'SUBSCRIPTION'
              ? (PERIOD_PRICE_LABEL[billingPeriodUnit] ?? billingPeriodUnit)
              : (PERIOD_PRICE_LABEL[sku.billingPeriodUnit ?? 'NONE'] ?? sku.billingPeriodUnit);

          return (
            <Card
              key={card.key}
              className="border border-default-200 bg-content1 shadow-none"
            >
              <Card.Header className="flex flex-col items-start gap-2">
                <Card.Title className="text-2xl font-bold text-foreground">
                  {sku.name}
                </Card.Title>
              </Card.Header>

              <Card.Content>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-default-500">
                    {sku.saleType === 'SUBSCRIPTION' ? '订阅价格' : '价格'}
                  </span>
                  <span className="text-right text-base text-foreground">
                    <span className="text-red-500 mr-2">¥</span>
                    <span className="font-bold text-2xl mr-2 text-red-500">{unitPrice}</span>
                    <span className="ml-1 text-default-500">
                      {sku.saleType === 'SUBSCRIPTION' ? `席位/${priceUnit}` : '席位'}
                    </span>
                  </span>
                </div>
              </Card.Content>

              {actions ? <Card.Footer>{actions}</Card.Footer> : null}
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto p-6">
      <div className="mb-8">
        <p className="text-sm text-default-500">当前计划</p>
        <h2 className="mt-1 text-3xl font-bold text-foreground">{planName}</h2>
      </div>

      {isLoading ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-8" />
        </div>
      ) : isError ? (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2">
          <p className="text-danger">加载失败</p>
          <p className="text-sm text-default-500">{error?.message || '未知错误'}</p>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-10">
          {periodUnits.length > 0 ? (
            <div className="flex flex-wrap items-center justify-center">
              <Segment selectedKey={billingPeriodUnit} onSelectionChange={handleBillingChange}>
                {periodUnits.map((unit) => (
                  <BillingSegmentItem key={unit} id={unit}>
                    <Segment.Separator />
                    {PERIOD_LABEL[unit] ?? unit}
                  </BillingSegmentItem>
                ))}
              </Segment>
            </div>
          ) : null}

          {renderCards()}
        </div>
      )}

      {!isLoading && !isError ? (
        <PlanFeatureCompare
          products={planCards.map((card) => ({
            id: String(card.displaySku.id),
            name: card.displaySku.name,
            featureConfig: readFeatureConfig(card.displaySku.metadata),
          }))}
        />
      ) : null}

      <PlanCheckoutModal
        open={checkoutModalOpen}
        product={checkoutProduct}
        billingPeriodUnit={toBillingPeriodUnit(checkoutProduct?.billingPeriodUnit)}
        initialAccounts={1}
        onCancel={() => {
          setCheckoutModalOpen(false);
          setCheckoutProduct(null);
        }}
        onPaid={() => {
          setCheckoutModalOpen(false);
          setCheckoutProduct(null);
          window.location.reload();
        }}
      />
    </div>
  );
}
